import { useList, useGo, useCustomMutation } from '@refinedev/core';
import { useState, useCallback } from 'react';
import {
  UserCheck,
  UserX,
  Clock,
  Eye,
  AlertTriangle,
  RefreshCw,
  Plus,
  Mail,
  ShoppingCart,
  CreditCard,
  Infinity as InfinityIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from '@/components/tables';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { notify } from '@/lib/notify';
import { translateErrorMessage } from '@/providers/notification-provider';
import {
  MEMBERSHIP_STATUS_LABELS,
  MEMBERSHIP_STATUS_COLORS,
  type ChannelMembership,
  type ChannelMembershipStatus,
  type BulkOperationResponse,
} from './types';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

interface TelegramChannel {
  id: string;
  title: string;
}

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

/**
 * ChannelMembershipsList
 *
 * Список channel-memberships с фильтрацией, bulk actions и расширенной статистикой
 */
export function ChannelMembershipsList() {
  const go = useGo();
  useScrollRestoration();
  const [page, setPage] = usePersistedState('channel-memberships:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('channel-memberships:sort', { field: 'updatedAt', order: 'desc' });
  const [statusFilter, setStatusFilter] = usePersistedState<string>('channel-memberships:statusFilter', 'all');
  const [channelFilter, setChannelFilter] = usePersistedState<string>('channel-memberships:channelFilter', 'all');
  const [search, setSearch] = usePersistedState('channel-memberships:search', '');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newMembershipUserId, setNewMembershipUserId] = useState('');
  const [newMembershipChannelId, setNewMembershipChannelId] = useState('');
  const [newMembershipSendInvite, setNewMembershipSendInvite] = useState(true);
  const [bulkConfirmDialog, setBulkConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    action: () => void;
  }>({ open: false, title: '', description: '', action: () => {} });
  const pageSize = 15;

  const filters = [];
  if (statusFilter !== 'all') {
    filters.push({ field: 'status', operator: 'eq' as const, value: statusFilter });
  }
  if (channelFilter !== 'all') {
    filters.push({ field: 'channelId', operator: 'eq' as const, value: channelFilter });
  }
  if (search) {
    filters.push({ field: 'search', operator: 'contains' as const, value: search });
  }

  const listResult = useList<ChannelMembership>({
    resource: 'channel-memberships',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const channelsResult = useList<TelegramChannel>({
    resource: 'telegram-channels',
    pagination: { pageSize: 100 },
  });

  const usersResult = useList<User>({
    resource: 'users',
    pagination: { pageSize: 100 },
  });

  const { mutate: bulkSendInvite } = useCustomMutation();
  const { mutate: bulkSync } = useCustomMutation();
  const { mutate: bulkUpdateStatus } = useCustomMutation();
  const { mutate: createMembership, mutation: createMembershipMutation } = useCustomMutation();

  const isCreating = createMembershipMutation.isPending;

  const data = listResult.result?.data ?? [];
  const total = listResult.result?.total ?? 0;
  const isLoading = listResult.query?.isLoading ?? false;
  const refetch = listResult.query?.refetch;

  const channels = channelsResult.result?.data ?? [];
  const users = usersResult.result?.data ?? [];

  const handleBulkSendInvite = useCallback((items: ChannelMembership[]) => {
    const ids = items.map((r) => Number(r.id));
    const eligibleIds = items
      .filter((r) => ['PENDING', 'EXPIRED', 'LEFT'].includes(r.status))
      .map((r) => Number(r.id));

    if (eligibleIds.length === 0) {
      notify.error('Нет записей, которым можно отправить приглашение');
      return;
    }

    setBulkConfirmDialog({
      open: true,
      title: 'Отправить приглашения?',
      description: `Будет отправлено ${eligibleIds.length} приглашений из ${ids.length} выбранных.`,
      action: () => {
        bulkSendInvite(
          { url: '/channel-memberships/bulk/send-invite', method: 'post', values: { ids: eligibleIds } },
          {
            onSuccess: (response) => {
              const data = response?.data as { data?: BulkOperationResponse } | undefined;
              if (data?.data?.summary) {
                notify.success(`Отправлено: ${data.data.summary.success}, ошибок: ${data.data.summary.failed}`);
              }
              refetch?.();
            },
            onError: (error) => {
              notify.error(`Ошибка: ${translateErrorMessage(error.message)}`);
            },
          }
        );
      },
    });
  }, [bulkSendInvite, refetch]);

  const handleBulkSync = useCallback((items: ChannelMembership[]) => {
    const ids = items.map((r) => Number(r.id));

    setBulkConfirmDialog({
      open: true,
      title: 'Синхронизировать статусы?',
      description: `Будет синхронизировано ${ids.length} записей с Telegram.`,
      action: () => {
        bulkSync(
          { url: '/channel-memberships/bulk/sync', method: 'post', values: { ids } },
          {
            onSuccess: (response) => {
              const data = response?.data as { data?: BulkOperationResponse } | undefined;
              if (data?.data?.summary) {
                notify.success(`Синхронизировано: ${data.data.summary.success}, ошибок: ${data.data.summary.failed}`);
              }
              refetch?.();
            },
            onError: (error) => {
              notify.error(`Ошибка: ${translateErrorMessage(error.message)}`);
            },
          }
        );
      },
    });
  }, [bulkSync, refetch]);

  const handleBulkUpdateStatus = useCallback((status: ChannelMembershipStatus, items: ChannelMembership[]) => {
    const ids = items.map((r) => Number(r.id));
    const statusLabel = MEMBERSHIP_STATUS_LABELS[status];

    setBulkConfirmDialog({
      open: true,
      title: `Изменить статус на "${statusLabel}"?`,
      description: `Будет изменён статус у ${ids.length} записей.`,
      action: () => {
        bulkUpdateStatus(
          { url: '/channel-memberships/bulk/status', method: 'patch', values: { ids, status } },
          {
            onSuccess: (response) => {
              const data = response?.data as { data?: BulkOperationResponse } | undefined;
              if (data?.data?.summary) {
                notify.success(`Обновлено: ${data.data.summary.success}, ошибок: ${data.data.summary.failed}`);
              }
              refetch?.();
            },
            onError: (error) => {
              notify.error(`Ошибка: ${translateErrorMessage(error.message)}`);
            },
          }
        );
      },
    });
  }, [bulkUpdateStatus, refetch]);

  const handleCreateMembership = useCallback(() => {
    if (!newMembershipUserId || !newMembershipChannelId) {
      notify.error('Выберите пользователя и канал');
      return;
    }

    createMembership(
      {
        url: '/channel-memberships',
        method: 'post',
        values: {
          userId: Number(newMembershipUserId),
          channelId: Number(newMembershipChannelId),
          sendInvite: newMembershipSendInvite,
        },
      },
      {
        onSuccess: () => {
          notify.success('Членство создано');
          setCreateDialogOpen(false);
          setNewMembershipUserId('');
          setNewMembershipChannelId('');
          setNewMembershipSendInvite(true);
          refetch?.();
        },
        onError: (error) => {
          notify.error(`Ошибка: ${translateErrorMessage(error.message)}`);
        },
      }
    );
  }, [newMembershipUserId, newMembershipChannelId, newMembershipSendInvite, createMembership, refetch]);

  const getStatusBadge = (status: ChannelMembershipStatus) => {
    const label = MEMBERSHIP_STATUS_LABELS[status] ?? status;
    const variant = MEMBERSHIP_STATUS_COLORS[status] ?? 'outline';

    if (status === 'ACTIVE') {
      return (
        <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">
          {label}
        </Badge>
      );
    }

    if (status === 'JOIN_REQUEST_SENT') {
      return (
        <Badge className="bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20">
          {label}
        </Badge>
      );
    }

    const safeVariant = variant === 'warning' ? 'secondary' : variant;
    return <Badge variant={safeVariant}>{label}</Badge>;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {return '—';}
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getSourceIcon = (sourceType: string | null) => {
    switch (sourceType) {
      case 'ORDER':
        return <ShoppingCart className="h-3.5 w-3.5" />;
      case 'SUBSCRIPTION':
        return <CreditCard className="h-3.5 w-3.5" />;
      default:
        return null;
    }
  };

  const getSourceLabel = (sourceType: string | null) => {
    switch (sourceType) {
      case 'ORDER':
        return 'Заказ';
      case 'SUBSCRIPTION':
        return 'Подписка';
      case 'ADMIN':
        return 'Админ';
      case 'PROMOTION':
        return 'Акция';
      default:
        return sourceType || '—';
    }
  };

  const getDaysRemainingBadge = (daysRemaining: number | null, accessEndsAt: string | null) => {
    if (daysRemaining === null && !accessEndsAt) {
      return (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <InfinityIcon className="h-3.5 w-3.5" />
          <span className="text-xs">Бессрочно</span>
        </div>
      );
    }

    if (daysRemaining === null) {return '—';}

    if (daysRemaining === 0) {
      return (
        <Badge variant="destructive" className="text-xs">
          Истёк
        </Badge>
      );
    }

    if (daysRemaining <= 3) {
      return (
        <Badge variant="destructive" className="text-xs">
          {daysRemaining} дн.
        </Badge>
      );
    }

    if (daysRemaining <= 7) {
      return (
        <Badge className="bg-orange-500/10 text-orange-600 text-xs">
          {daysRemaining} дн.
        </Badge>
      );
    }

    if (daysRemaining <= 30) {
      return (
        <Badge className="bg-yellow-500/10 text-yellow-600 text-xs">
          {daysRemaining} дн.
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="text-xs">
        {daysRemaining} дн.
      </Badge>
    );
  };

  const columns: Column<ChannelMembership>[] = [
    {
      key: 'user.firstName',
      header: 'Пользователь',
      sortable: true,
      render: (_, membership) => {
        const name = [membership.user?.firstName, membership.user?.lastName]
          .filter(Boolean)
          .join(' ');
        return (
          <div>
            <div className="font-medium">{name || '—'}</div>
            {membership.user?.username && (
              <div className="text-xs text-muted-foreground">
                @{membership.user.username}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'channel.title',
      header: 'Канал',
      sortable: true,
      render: (_, membership) => (
        <div className="font-medium">{membership.channel?.title || '—'}</div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      sortable: true,
      render: (value, membership) => (
        <div className="flex items-center gap-2">
          {getStatusBadge(value as ChannelMembershipStatus)}
          {membership.lastError && (
            <span title={membership.lastError} className="text-destructive">
              <AlertTriangle className="h-4 w-4" />
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'daysRemaining',
      header: 'Осталось',
      sortable: false,
      render: (_, membership) => getDaysRemainingBadge(membership.daysRemaining, membership.accessEndsAt),
    },
    {
      key: 'accessSourceType',
      header: 'Источник',
      sortable: false,
      render: (_, membership) => {
        const icon = getSourceIcon(membership.accessSourceType);
        const label = getSourceLabel(membership.accessSourceType);
        return (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            {icon}
            <span className="text-xs">{label}</span>
          </div>
        );
      },
    },
    {
      key: 'joinedAt',
      header: 'Вступил',
      sortable: true,
      render: (value) => formatDate(value as string | null),
    },
    {
      key: 'accessEndsAt',
      header: 'Доступ до',
      sortable: false,
      render: (_, membership) => membership.accessEndsAt ? formatDate(membership.accessEndsAt) : '—',
    },
    {
      key: 'updatedAt',
      header: 'Обновлено',
      sortable: true,
      render: (value) => formatDate(value as string),
    },
  ];

  const bulkActions: BulkAction<ChannelMembership>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/users/show/${items[0].userId}?tab=access&membership=${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Отправить приглашения',
      icon: <Mail className="h-4 w-4" />,
      onClick: (items) => handleBulkSendInvite(items),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Синхронизировать',
      icon: <RefreshCw className="h-4 w-4" />,
      onClick: (items) => handleBulkSync(items),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Установить ACTIVE',
      icon: <UserCheck className="h-4 w-4" />,
      onClick: (items) => handleBulkUpdateStatus('ACTIVE', items),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Установить LEFT',
      icon: <UserX className="h-4 w-4" />,
      onClick: (items) => handleBulkUpdateStatus('LEFT', items),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Установить EXPIRED',
      icon: <Clock className="h-4 w-4" />,
      onClick: (items) => handleBulkUpdateStatus('EXPIRED', items),
      showOnlyWhenSelected: true,
    },
  ];

  const filtersCount =
    (statusFilter === 'all' ? 0 : 1) + (channelFilter === 'all' ? 0 : 1);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Создать
        </Button>
      </div>

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Поиск по пользователю..."
        filters={
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="membership-status">Статус</Label>
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="membership-status">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="PENDING">Ожидает</SelectItem>
                  <SelectItem value="JOIN_REQUEST_SENT">Заявка отправлена</SelectItem>
                  <SelectItem value="ACTIVE">Активно</SelectItem>
                  <SelectItem value="LEFT">Вышел</SelectItem>
                  <SelectItem value="KICKED">Исключён</SelectItem>
                  <SelectItem value="BOT_BLOCKED">Бот заблокирован</SelectItem>
                  <SelectItem value="EXPIRED">Истекло</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="membership-channel">Канал</Label>
              <Select
                value={channelFilter}
                onValueChange={(v) => {
                  setChannelFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="membership-channel">
                  <SelectValue placeholder="Канал" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все каналы</SelectItem>
                  {channels.map((channel: TelegramChannel) => (
                    <SelectItem key={channel.id} value={channel.id}>
                      {channel.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        filtersCount={filtersCount}
        onResetFilters={() => {
          setStatusFilter('all');
          setChannelFilter('all');
          setSearch('');
          setPage(1);
        }}
      />

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(membership) =>
          go({ to: `/users/show/${membership.userId}?tab=access&membership=${membership.id}` })
        }
        emptyMessage="Нет записей о членстве"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {/* Create Membership Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Создать членство</DialogTitle>
            <DialogDescription>
              Добавьте пользователя в канал вручную
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Пользователь</Label>
              <Select value={newMembershipUserId} onValueChange={setNewMembershipUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите пользователя" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u: User) => (
                    <SelectItem key={u.id} value={u.id}>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || `ID: ${u.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Канал</Label>
              <Select value={newMembershipChannelId} onValueChange={setNewMembershipChannelId}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите канал" />
                </SelectTrigger>
                <SelectContent>
                  {channels.map((ch: TelegramChannel) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      {ch.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="send-invite"
                checked={newMembershipSendInvite}
                onChange={(e) => setNewMembershipSendInvite(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="send-invite">Отправить приглашение сразу</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleCreateMembership} disabled={isCreating}>
              {isCreating ? 'Создание...' : 'Создать'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Confirmation Dialog */}
      <AlertDialog
        open={bulkConfirmDialog.open}
        onOpenChange={(open) => setBulkConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkConfirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>{bulkConfirmDialog.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={bulkConfirmDialog.action}>
              Подтвердить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
